'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { api } from '../configs/api'
import { useAuth } from './AuthContext'

type ProfessionalDataType = {
    id: string
    name: string
    image?: string
    situation?: string
    email?: string
    phone?: string
    company: {
        id: string
        name: string
    }
}

export type ProfessionalDetailType = {
    professional: {
        id: number
        name: string
        image: string | null
        email: string
    }
    notes: {
        id: number
        businessID: number
        title: string
        name: string
        date: string
        dateNotify: string | null
        description: string
        users: {
            id: number
            name: string
            image?: string
        }[]
        status: 0 | 1
        isExpired: boolean
    }[]
    business: {
        id: number
        pipeline: string
        step: string
        color: string
        description: string | null
        products: {
            id: number
            name: string
        }[]
        targets: {
            id: number
            name: string
            color: string
        }[]
    }[]
}

type ProfessionalType = {
    professionals: ProfessionalDataType[]
    setProfessionals: (value: ProfessionalDataType[]) => void
    filteredProfessionals: ProfessionalDataType[]
    setFilteredProfessionals: (value: ProfessionalDataType[]) => void
    loadProfessionals: () => Promise<void>
    getProfessional: (id: string) => Promise<void>
    detail: ProfessionalDetailType | null
    setDetail: React.Dispatch<React.SetStateAction<ProfessionalDetailType | null>>

    // setDetail: (value: ProfessionalDetailType | null) => void
}

const Professional = createContext<ProfessionalType | undefined>(undefined)

export const ProfessionalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity } = useAuth()
    const [professionals, setProfessionals] = useState<ProfessionalDataType[]>([])
    const [filteredProfessionals, setFilteredProfessionals] = useState<ProfessionalDataType[]>([])
    const [detail, setDetail] = useState<ProfessionalDetailType | null>(null)

    const loadProfessionals = async () => {
        if (!unity) return

        const response = await fetch(`${api}professional/getData`, {
            method: 'POST',
            body: JSON.stringify({
                unityID: unity.unityID
            })
        })
        const result = await response.json()
        setProfessionals(result.list)
        setFilteredProfessionals(result.list)
    }

    const getProfessional = useCallback(async (id: string) => {
        const response = await fetch(`${api}professional/getDetail`, {
            method: 'POST',
            body: JSON.stringify({ id })
        })
        const result = await response.json()
        setDetail(result.detail)
    }, [])

    useEffect(() => {
        //* Carrega profissionais
        loadProfessionals()
    }, [unity])

    return (
        <Professional.Provider
            value={{
                professionals,
                setProfessionals,
                filteredProfessionals,
                setFilteredProfessionals,
                loadProfessionals,
                getProfessional,
                detail,
                setDetail
            }}
        >
            {children}
        </Professional.Provider>
    )
}

export function useProfessional() {
    const context = useContext(Professional)
    if (context === undefined) {
        throw new Error('useCreate must be used within a ProfessionalProvider')
    }
    return context
}
